// RETE CHE NON SI SPEGNE — versione web (v3.9.1).
//
// Il client web parlava con UN solo indirizzo e con 25 secondi di pazienza: se quel nodo
// si spegneva, o la rete dell'utente non lo raggiungeva, il sito restava fermo fino al
// timeout e poi si arrendeva. Qui si aggiungono tre cose, nell'ordine in cui contano:
//
//  1. SENTINELLA: un filo in sottofondo misura ogni 3 secondi tutti gli ingressi pubblici
//     con 900 ms di pazienza. Quando quello di casa smette di rispondere, l'ingresso
//     cambia PRIMA che l'utente prema qualcosa: la scelta dell'alternativa è già fatta.
//  2. GARA SULLE LETTURE: una lettura che tarda più di 800 ms viene affiancata dalla
//     stessa lettura fatta passare da un altro nodo pubblico; vince chi risponde prima.
//     Le scritture non gareggiano mai: duplicarle vorrebbe dire rischiare un doppione.
//  3. TRAMITE: se il nodo di casa non risponde a livello di rete, la richiesta viene
//     rifatta attraverso un altro nodo, che la inoltra. Le buste sono già cifrate: il
//     nodo di passaggio si comporta da router e non può leggere niente.
//
// Il gettone di sessione vale solo sul nodo che l'ha emesso: quando si passa a un nodo di
// scorta lo si butta, e la sessione si rifà con una firma sola (il file chiave non lascia
// mai il browser). L'identità è già in copia sul nodo di scorta, quindi la firma passa.
import axios from "axios";

const ATTESA_PING = 900;
const RITMO_SENTINELLA = 3000;
const GARA_DOPO = 800;
const CADUTE_PER_CAMBIO = 2;
const MEMORIA = "lat.mesh.nodi.v1";

const norm = (u) => String(u || "").trim().replace(/\/+$/, "");

let _base = "";
let _casa = null;          // dove si dovrebbe stare, quando si lavora da una scorta
let _pool = [];
let _salute = new Map();   // url -> { ok, ms, at }
let _cadute = 0;
let _timer = null;
let _client = null;

function ricorda() {
  try { localStorage.setItem(MEMORIA, JSON.stringify(_pool)); } catch { /* niente */ }
}
function ricordati() {
  try {
    const v = JSON.parse(localStorage.getItem(MEMORIA) || "[]");
    return Array.isArray(v) ? v.map(norm).filter(Boolean) : [];
  } catch { return []; }
}

export function nodiNoti() { return _pool.slice(); }
export function nodoInUso() { return _base; }
export function nodoDiCasa() { return _casa || _base; }
export function statoNodi() {
  const nodi = {};
  for (const [u, s] of _salute.entries()) nodi[u] = s;
  return { base: _base, casa: nodoDiCasa(), nodi, tramite: tramiteCaldo() };
}

async function misura(u) {
  const url = norm(u);
  const t0 = Date.now();
  try {
    const r = await axios.get(`${url}/api/health`, { timeout: ATTESA_PING });
    _salute.set(url, { ok: !!r && r.status === 200, ms: Date.now() - t0, at: Date.now() });
  } catch {
    _salute.set(url, { ok: false, ms: 99999, at: Date.now() });
  }
  return _salute.get(url);
}

/// L'alternativa sana più veloce, già misurata: nell'emergenza non si prova niente.
export function tramiteCaldo() {
  let best = null;
  for (const u of _pool) {
    const url = norm(u);
    if (url === _base) continue;
    const s = _salute.get(url);
    if (!s || !s.ok) continue;
    if (!best || s.ms < best.ms) best = { url, ms: s.ms };
  }
  return best ? best.url : null;
}

function cambiaIngresso(url) {
  const nuovo = norm(url);
  if (!nuovo || nuovo === _base) return false;
  if (!_casa) _casa = _base;
  _base = nuovo;
  if (_client) _client.defaults.baseURL = `${_base}/api`;
  // Il gettone vale solo sul nodo che l'ha emesso: si butta, si rifà con una firma.
  try { sessionStorage.removeItem("lattice_sc_token"); localStorage.removeItem("lattice_sc_token"); } catch { /* niente */ }
  try { window.dispatchEvent(new CustomEvent("lattice:nodo-cambiato", { detail: { nodo: _base, casa: _casa } })); } catch { /* niente */ }
  return true;
}

function tornaACasa() {
  if (!_casa) return false;
  const casa = _casa;
  _casa = null;
  _base = casa;
  if (_client) _client.defaults.baseURL = `${_base}/api`;
  try { sessionStorage.removeItem("lattice_sc_token"); localStorage.removeItem("lattice_sc_token"); } catch { /* niente */ }
  try { window.dispatchEvent(new CustomEvent("lattice:nodo-cambiato", { detail: { nodo: _base, casa: null } })); } catch { /* niente */ }
  return true;
}

async function giro() {
  const base = _base;
  const st = await misura(base);
  await Promise.all(_pool.map(norm).filter((u) => u !== base).map(misura));
  if (_casa) {
    const h = _salute.get(norm(_casa));
    if (h && h.ok) { tornaACasa(); _cadute = 0; return; }
  }
  if (st && st.ok) { _cadute = 0; return; }
  _cadute += 1;
  if (_cadute < CADUTE_PER_CAMBIO) return;
  const via = tramiteCaldo();
  if (via) { _cadute = 0; cambiaIngresso(via); }
}

export function avviaSentinella() {
  if (_timer) return;
  _timer = setInterval(() => { giro().catch(() => {}); }, RITMO_SENTINELLA);
  giro().catch(() => {});
}

/// Gli ingressi pubblici li dichiara il nodo stesso: così un nodo nuovo entra nell'elenco
/// senza che nessuno aggiorni l'app.
async function imparaIngressi() {
  try {
    const r = await axios.get(`${_base}/api/public/mesh/entrypoints`, { timeout: 4000 });
    const urls = ((r.data && r.data.entrypoints) || []).map((e) => norm(e.url)).filter(Boolean);
    if (urls.length) {
      _pool = Array.from(new Set(urls.concat(_pool)));
      ricorda();
    }
  } catch { /* si resta con l'elenco che si ha */ }
}

/// La stessa richiesta, inoltrata da un altro nodo pubblico.
async function viaTramite(via, cfg, attesa) {
  let path = `/api${String(cfg.url || "").startsWith("/") ? "" : "/"}${cfg.url || ""}`;
  if (cfg.params && typeof cfg.params === "object") {
    const q = Object.entries(cfg.params).filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
    if (q) path += (path.includes("?") ? "&" : "?") + q;
  }
  let body = {};
  try { body = typeof cfg.data === "string" ? JSON.parse(cfg.data) : (cfg.data || {}); } catch { body = {}; }
  const auth = String((cfg.headers && (cfg.headers.Authorization || cfg.headers.authorization)) || "").replace(/^Bearer\s+/i, "");
  const r = await axios.post(`${via}/api/public/mesh/relay`, {
    home: nodoDiCasa(), method: String(cfg.method || "get").toUpperCase(), path, body, auth,
  }, { timeout: attesa || 20000 });
  const out = (r && r.data) || {};
  const st = Number(out.status || 0);
  if (st >= 400) {
    const e = new Error("errore dal nodo di casa");
    e.response = { status: st, data: out.body };
    throw e;
  }
  return { data: out.body, status: st || 200, statusText: "OK", headers: {}, config: cfg };
}

const erroreDiRete = (e) => {
  if (!e || e.response) return false;
  const c = String(e.code || "");
  return c !== "ERR_CANCELED" && !/canceled|aborted/i.test(String(e.message || ""));
};

/// Si installa sul client axios del sito: da qui in avanti il web ha la stessa rete di
/// sicurezza dell'app.
export function installaMesh(client, backendUrl, altri = []) {
  _client = client;
  _base = norm(backendUrl);
  _pool = Array.from(new Set([_base, ...altri.map(norm), ...ricordati()].filter(Boolean)));

  client.interceptors.response.use((r) => r, async (e) => {
    const cfg = e && e.config;
    if (!erroreDiRete(e) || !cfg || cfg.__viaRelay || cfg.responseType === "arraybuffer" || cfg.responseType === "blob") throw e;
    const via = tramiteCaldo();
    if (!via) throw e;
    try {
      return await viaTramite(via, cfg, 20000);
    } catch (e2) {
      // Il tramite c'è ma il nodo di casa non risponde nemmeno a lui: è spento. Si passa
      // a lavorare direttamente dalla scorta.
      if (e2 && e2.response && e2.response.status === 502) cambiaIngresso(via);
      throw e;
    }
  });

  const originale = client.get.bind(client);
  client.get = (url, cfg = {}) => {
    const prima = originale(url, cfg);
    if (cfg.__senzaGara || cfg.responseType === "arraybuffer" || cfg.responseType === "blob") return prima;
    return new Promise((resolve, reject) => {
      let chiuso = false;
      const vinci = (r) => { if (!chiuso) { chiuso = true; resolve(r); } };
      const perdi = (x) => { if (!chiuso) { chiuso = true; reject(x); } };
      prima.then(vinci, perdi);
      const t = setTimeout(() => {
        if (chiuso) return;
        const via = tramiteCaldo();
        if (!via) return;
        viaTramite(via, { url, method: "get", params: cfg.params, headers: cfg.headers }, 8000)
          .then(vinci, () => { /* la gara la vince l'altro */ });
      }, GARA_DOPO);
      prima.then(() => clearTimeout(t), () => clearTimeout(t));
    });
  };

  imparaIngressi().finally(avviaSentinella);
  return client;
}
